import { InternalAxiosRequestConfig, AxiosError } from 'axios';
import { Subject } from 'rxjs';
import { Axios } from '@cbridge-utils/axios-observable';
import { AuthService } from '@cbridge-packages/auth';

export const logoutSubject = new Subject<void>();

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

interface FailedRequest {
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
  config: InternalAxiosRequestConfig;
}

export class RefreshTokenEngine {
  private api: Axios;

  private auth: AuthService;

  private isRefreshing = false;

  private failedQueue: FailedRequest[] = [];

  protected processQueue(error: Error | null) {
    this.failedQueue.forEach(({ resolve, reject, config }) => {
      if (error) {
        reject(error);
      } else {
        resolve(this.api.requestInternal(config));
      }
    });
    this.failedQueue = [];
  }

  public constructor(api: Axios, auth: AuthService) {
    this.api = api;
    this.auth = auth;
  }

  public async handleUnauthorized(error: AxiosError) {
    const originalRequest = error.config as RetryableRequestConfig;

    if (error?.response?.status === 401 && !originalRequest._retry) {
      if (this.isRefreshing) {
        return new Promise((resolve, reject) => {
          this.failedQueue.push({ resolve, reject, config: originalRequest });
        });
      }

      originalRequest._retry = true;
      this.isRefreshing = true;

      try {
        await this.auth.updateToken(true);
        this.processQueue(null);

        return this.api.requestInternal(originalRequest);
      } catch (refreshError) {
        this.processQueue(refreshError as Error);
        logoutSubject.next();

        return Promise.reject(refreshError);
      } finally {
        this.isRefreshing = false;
      }
    }
    return false;
  }
}
